import { ItemCategory } from "@/parser";

export interface StatMatcher {
  string: string;
  advanced?: string;
  negate?: true;
  value?: number;
  oils?: string; // Amulet anointment
}

export interface StatTier {
  ilvl: number;
  id: string;
  name: string;
  values: number[][];
  items: string[];
}

export interface StatTierMod {
  id: string;
  generation?: string;
  items: {
    [type: string]: number;
  };
  mods: StatTier[];
}

export enum StatBetter {
  NegativeRoll = -1,
  PositiveRoll = 1,
  NotComparable = 0,
}

export interface Stat {
  ref: string;
  dp?: true;
  matchers: StatMatcher[];
  better: StatBetter;
  fromAreaMods?: true;
  fromUberAreaMods?: true;
  fromHeistAreaMods?: true;
  anointments?: Array<{ roll: number; oils: string }>; // Ring anointments
  trade: {
    inverted?: true;
    option?: true;
    count?: true;
    ids: {
      [type: string]: string[];
    };
  };
}

export interface DropEntry {
  query: string[];
  items: string[];
}

export interface BaseType {
  name: string;
  refName: string;
  namespace: "DIVINATION_CARD" | "CAPTURED_BEAST" | "UNIQUE" | "ITEM" | "GEM";
  icon: string;
  w?: number;
  h?: number;
  tradeTag?: string;
  tradeDisc?: string;
  disc?: {
    propAR?: true;
    propEV?: true;
    propES?: true;
    hasImplicit?: { ref: Stat["ref"] };
    hasExplicit?: { ref: Stat["ref"] };
    sectionText?: string;
    mapTier?: "W" | "Y" | "R";
  };
  // extra info
  craftable?: {
    category: ItemCategory;
    corrupted?: true;
    uniqueOnly?: true;
  };
  unique?: {
    base: BaseType["refName"];
    fixedStats?: string[];
  };
  map?: {
    screenshot?: string;
    tier?: number;
  };
  gem?: {
    vaal?: true;
    awakened?: true;
    transfigured?: true;
    normalVariant?: BaseType["refName"];
  };
  armour?: {
    ar?: [min: number, max: number];
    ev?: [min: number, max: number];
    es?: [min: number, max: number];
  };
  augment?: AugmentLineData[];
  tags: string[];
}

export interface AugmentLineData {
  categories: ItemCategory[];
  string: string;
  values: number[];
  tradeId?: string[];
}

export interface TranslationDict {
  RARITY_NORMAL: string;
  RARITY_MAGIC: string;
  RARITY_RARE: string;
  RARITY_UNIQUE: string;
  RARITY_GEM: string;
  RARITY_CURRENCY: string;
  RARITY_DIVCARD: string;
  RARITY_QUEST: string;
  MAP_TIER: string;
  RARITY: string;
  ITEM_CLASS: string;
  ITEM_LEVEL: string;
  CORPSE_LEVEL: string;
  TALISMAN_TIER: string;
  GEM_LEVEL: string;
  STACK_SIZE: string;
  SOCKETS: string;
  QUALITY: string;
  PHYSICAL_DAMAGE: string;
  ELEMENTAL_DAMAGE: string;
  CRIT_CHANCE: string;
  ATTACK_SPEED: string;
  ARMOUR: string;
  EVASION: string;
  ENERGY_SHIELD: string;
  RUNIC_WARD: string;
  BLOCK_CHANCE: string;
  CORRUPTED: string;
  UNIDENTIFIED: RegExp;
  ITEM_SUPERIOR: RegExp;
  ITEM_EXCEPTIONAL: RegExp;
  MAP_BLIGHTED: RegExp;
  MAP_BLIGHT_RAVAGED: RegExp;
  INFLUENCE_SHAPER: string;
  INFLUENCE_ELDER: string;
  INFLUENCE_CRUSADER: string;
  INFLUENCE_HUNTER: string;
  INFLUENCE_REDEEMER: string;
  INFLUENCE_WARLORD: string;
  SECTION_SYNTHESISED: string;
  ITEM_SYNTHESISED: RegExp;
  VEILED_PREFIX: string;
  VEILED_SUFFIX: string;
  FLASK_CHARGES: RegExp;
  METAMORPH_HELP: string;
  BEAST_HELP: string;
  VOIDSTONE_HELP: string;
  METAMORPH_BRAIN: RegExp;
  METAMORPH_EYE: RegExp;
  METAMORPH_LUNG: RegExp;
  METAMORPH_HEART: RegExp;
  METAMORPH_LIVER: RegExp;
  CANNOT_USE_ITEM: string;
  QUALITY_ANOMALOUS: RegExp;
  QUALITY_DIVERGENT: RegExp;
  QUALITY_PHANTASMAL: RegExp;
  AREA_LEVEL: string;
  HEIST_WINGS_REVEALED: string;
  HEIST_TARGET: string;
  HEIST_BLUEPRINT_ENCHANTS: string;
  HEIST_BLUEPRINT_TRINKETS: string;
  HEIST_BLUEPRINT_GEMS: string;
  HEIST_BLUEPRINT_REPLICAS: string;
  MIRRORED: string;
  MODIFIER_LINE: RegExp;
  PREFIX_MODIFIER: string;
  SUFFIX_MODIFIER: string;
  CRAFTED_MODIFIER: string;
  FRACTURED_MODIFIER: string;
  DESECRATED_MODIFIER: string;
  UNSCALABLE_VALUE: string;
  CORRUPTED_IMPLICIT: string;
  MODIFIER_INCREASED: RegExp;
  INCURSION_OPEN: string;
  INCURSION_OBSTRUCTED: string;
  EATER_IMPLICIT: RegExp;
  EXARCH_IMPLICIT: RegExp;
  ELDRITCH_MOD_R1: string;
  ELDRITCH_MOD_R2: string;
  ELDRITCH_MOD_R3: string;
  ELDRITCH_MOD_R4: string;
  ELDRITCH_MOD_R5: string;
  ELDRITCH_MOD_R6: string;
  SENTINEL_CHARGE: string;
  SHAPER_MODS: string[];
  ELDER_MODS: string[];
  CRUSADER_MODS: string[];
  HUNTER_MODS: string[];
  REDEEMER_MODS: string[];
  WARLORD_MODS: string[];
  DELVE_MODS: string[];
  VEILED_MODS: string[];
  INCURSION_MODS: string[];
  FOIL_UNIQUE: string;
  UNMODIFIABLE: string;
  REQUIREMENTS: string;
  REQUIRES: string;
  CHARM_SLOTS: string;
  BASE_SPIRIT: string;
  LIFE_TAG: string;
  MANA_TAG: string;
  ARMOUR_TAG: string;
  EVASION_TAG: string;
  ENERGY_SHIELD_TAG: string;
  PHYSICAL_TAG: string;
  FIRE_TAG: string;
  COLD_TAG: string;
  LIGHTNING_TAG: string;
  CHAOS_TAG: string;
  ATTACK_TAG: string;
  CASTER_TAG: string;
  SPEED_TAG: string;
  ATTRIBUTE_TAG: string;
  MINION_TAG: string;
  // ---
  CHAT_SYSTEM: RegExp;
  CHAT_TRADE: RegExp;
  CHAT_GLOBAL: RegExp;
  CHAT_PARTY: RegExp;
  CHAT_GUILD: RegExp;
  CHAT_WHISPER_TO: RegExp;
  CHAT_WHISPER_FROM: RegExp;
  CHAT_WEBTRADE_GEM: RegExp;
  // ---
  FIRE_DAMAGE: string;
  LIGHTNING_DAMAGE: string;
  COLD_DAMAGE: string;
  PRICE_NOTE: string;
  WAYSTONE_TIER: string;
  WAYSTONE_REVIVES: string;
  WAYSTONE_PACK_SIZE: string;
  WAYSTONE_MAGIC_MONSTERS: string;
  WAYSTONE_RARE_MONSTERS: string;
  WAYSTONE_DROP_CHANCE: string;
  WAYSTONE_RARITY: string;
  WAYSTONE_MONSTER_RARITY: string;
  WAYSTONE_EFFECTIVENESS: string;
  // WAYSTONE_GOLD: string;
  TIMELESS_RADIUS: string;
  TIMELESS_SMALL_PASSIVES: string;
  TIMELESS_NOTABLE_PASSIVES: string;
  GRANTS_SKILL: string;
  RELOAD_SPEED: string;
  FRACTURED_ITEM: string;
  SANCTIFIED: string;
  HYPHEN: string;
  LOG_LEVEL_UP: RegExp;
  LOG_ZONE_GEN: RegExp;
  DOUBLE_CORRUPTED: string;
  IMPLICIT_MODIFIER: string;
  ENCHANT_MODIFIER: string;
  CORRUPTED_MODIFIER: string;
  REQUIRES_LINE: RegExp;
  UNIQUE_MODIFIER: string;
  VAAL_UNIQUE_MODIFIER: string;
  TRIAL_COUNT: string;
  ULTIMATUM_VICTORIOUS: string;
  ULTIMATUM_COWARDLY: string;
  ULTIMATUM_DEADLY: string;
}

export interface Filter {
  id: string;
  value: {
    weight: number;
  };
  disabled: boolean;
}

export interface ItemCategoryToEmptyPrefix {
  [id: string]: {
    filters: Filter[];
    type: "not";
    disabled?: boolean;
  };
}

export interface AugmentSingleValue {
  [id: string]: {
    augment: string;
    baseStat: string;
    values: number[];
    id: string;
    type: "armour" | "weapon";
  };
}

export interface AugmentData {
  augment: string;
  refName: string;
  baseStat: string;
  values: number[];
  id: string;
  categories: ItemCategory[];
  icon: string;
}
/**
 * Key for each augment, Iron, Lesser Rebirth, Soul core of xx
 *
 * Value is each option that the value could be for, ie stat for armour or weapon.
 */
export interface AugmentDataByAugment {
  [augment: string]: AugmentData[] | undefined;
}
export interface AugmentDataByTradeId {
  [tradeId: string]:
    | Array<{
        refName: string;
        augment: string;
        baseStat: string;
        values: number[];
        id: string;
        categories: ItemCategory[];
        icon: string;
      }>
    | undefined;
}

export interface AugmentGroup<T> {
  Rune: {
    Lesser: T[];
    Normal: T[];
    Greater: T[];
    Perfect: T[];
    Other: T[];
  };
  Legacy: T[];
  SoulCore: {
    Normal: T[];
    Special: T[];
  };
  Idol: T[];
  Other: T[];
}

export interface CatalystGroup<T> {
  Normal: T;
  Refined: T;
}
